/**
 * Web onboarding completion gate.
 * AuthGuard remounts when leaving `/onboarding` for ProtectedPage routes
 * (`/training-plans`, `/plan-generator`, …). A stale `/api/auth/me` that
 * still has `onboardingCompletedAt: null` must not bounce a finished rider.
 */

export const ONBOARDING_JUST_COMPLETED_KEY = 'onboarding_just_completed';

/** @param {unknown} user */
export function isWebOnboardingComplete(user) {
  const at = user && typeof user === 'object' ? user.onboardingCompletedAt : null;
  return at != null && at !== '';
}

export function readOnboardingJustCompletedFlag() {
  try {
    return sessionStorage.getItem(ONBOARDING_JUST_COMPLETED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markOnboardingJustCompleted() {
  try {
    sessionStorage.setItem(ONBOARDING_JUST_COMPLETED_KEY, 'true');
  } catch {
    /* private mode / blocked storage */
  }
}

export function clearOnboardingJustCompletedFlag() {
  try {
    sessionStorage.removeItem(ONBOARDING_JUST_COMPLETED_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Keep a completed timestamp when live `/me` is stale-null.
 * @param {object|null|undefined} liveUser
 * @param {object|null|undefined} cachedUser
 */
export function mergeOnboardingCompletedAt(liveUser, cachedUser) {
  if (!liveUser || typeof liveUser !== 'object') return liveUser;
  if (isWebOnboardingComplete(liveUser)) return liveUser;
  if (
    isWebOnboardingComplete(cachedUser) &&
    cachedUser.id &&
    liveUser.id &&
    cachedUser.id === liveUser.id
  ) {
    return { ...liveUser, onboardingCompletedAt: cachedUser.onboardingCompletedAt };
  }
  return liveUser;
}

/**
 * Persist a real completed-at from onboarding-complete or `/me`.
 * Does not invent a timestamp when both sources are missing.
 * @param {string|null|undefined} completedAt
 * @param {object|null|undefined} liveUser
 * @returns {object|null}
 */
export function persistOnboardingCompletedAt(completedAt, liveUser) {
  if (completedAt == null || completedAt === '') return null;
  let cached = {};
  try {
    cached = JSON.parse(localStorage.getItem('user') || 'null') || {};
  } catch {
    cached = {};
  }
  const next = {
    ...cached,
    ...(liveUser && typeof liveUser === 'object' ? liveUser : {}),
    onboardingCompletedAt:
      (liveUser && liveUser.onboardingCompletedAt) ||
      cached.onboardingCompletedAt ||
      completedAt,
  };
  localStorage.setItem('user', JSON.stringify(next));
  return next;
}

/**
 * Just-completed is a `/me` cache-bust only. Redirect uses the completed stamp.
 * @param {{
 *   user?: object|null,
 *   pathname?: string,
 * }} [opts]
 */
export function shouldRedirectToOnboarding({ user, pathname } = {}) {
  if (pathname === '/onboarding') return false;
  return !isWebOnboardingComplete(user);
}
